import { Injectable } from "@angular/core";
import { Sort } from "@angular/material/sort";
import { ConfigurationService } from "@app/configuration/configuration.service";
import { LookupModel } from "@app/configuration/models/configuration.models";
import { PagedList } from "@app/core";
import { IAndRListService } from '@app/i-and-rs/list/services/i-and-rs-list.service';
import { IAndR } from '@app/i-and-rs/list/types/i-and-rs';
import { IAndRListFilters } from '@app/i-and-rs/list/types/i-and-rs-filters';
import { RootStoreState, ViewStateComponentStore, ViewStateComponentStoreState } from "@app/root-store";
import { CaseListFilterOptions } from "@app/shared/types/case-list-filter-options";
import { Pagination, Selected } from "@app/types/core";
import { PopoverFilterOption } from '@app/shared/filters';
import { tapResponse } from '@ngrx/component-store';
import { Store } from "@ngrx/store";
import { forkJoin, Observable } from "rxjs";
import { concatMap, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { GetIAndRsListPayload } from "../types/i-and-rs-params";

export interface IAndRListState extends ViewStateComponentStoreState {
   iAndRCases: IAndR[];
   payload: GetIAndRsListPayload;
   options: CaseListFilterOptions;
   userRoles: LookupModel[];
   userRolesResponses: Response[];
   count: number;
   loading: boolean,
   toggle: boolean,
}

export interface IAndRListViewState {
   filters: IAndRListFilters;
   selected: Selected;
   search: string;
   activeOnly: boolean;
   pagination: Pagination;
   sort: Sort;
}

const initialState: IAndRListState = {
   iAndRCases: [],
   payload: {
      pagination: null,
      sort: null,
      filters: {},
      selected: {},
      search: null
   },
   options: {
      legalTeams: null,
      fundingPrograms: null,
      caseStatuses: null
   },
   userRoles: [],
   userRolesResponses: [],
   count: 0,
   loading: false,
   toggle: false,
   viewId: null
}

const initialViewState: IAndRListViewState = {
   filters: {
      number: null,
      client: null,
      caseName: null,
      primaryStaff: null,
      legalTeam: null,
      funder: null,
      status: null,
      opened: null,
      closed: null
   },
   selected: {
      users: [],
      roles: []
   },
   search: null,
   activeOnly: true,
   pagination: { page: 1, pageSize: 25 },
   sort: { active: 'opened', direction: 'desc' }
};

@Injectable()
export class IAndRStateStore extends ViewStateComponentStore<IAndRListState, IAndRListViewState> {

   constructor(
      rootStore: Store<RootStoreState.State>,
      private service: IAndRListService,
      private configurationService: ConfigurationService,) {
      super(rootStore, initialViewState);
   }

   init(viewId: string): void {
      this.initCore({
         ...initialState,
         viewId
      });
      this.patchState({ loading: true });
      forkJoin([
         this.service.getFilterOptions(),
         this.configurationService.getLookupType('involvement-user-role')]).subscribe(
            ([options, types]) => {
               this.patchState({ options, userRoles: types.lookups, loading: false }), error => console.log(error);
            })
   }

   readonly legalTeamOptions$: Observable<PopoverFilterOption[]> = this.select(
      this.state$,
      ({ options }) => options.legalTeams?.map(value => ({ value, text: value }))
   );

   readonly funderOptions$: Observable<PopoverFilterOption[]> = this.select(
      this.state$,
      ({ options }) => options.fundingPrograms?.map(value => ({ value, text: value }))
   );

   readonly statusOptions$: Observable<PopoverFilterOption[]> = this.select(
      this.state$,
      ({ options }) => options.caseStatuses?.map(value => ({ value, text: value }))
   );

   readonly vm$ = this.select(
      this.legalTeamOptions$,
      this.funderOptions$,
      this.statusOptions$,
      this.state$,
      this.viewState$,
      (legalTeamOptions, funderOptions, statusOptions, { iAndRCases, payload, userRoles, count, loading, toggle }, { filters, selected, activeOnly, pagination, sort }) => (
         { legalTeamOptions, funderOptions, statusOptions, iAndRCases, payload, userRoles, count, loading, toggle, filters, selected, activeOnly, pagination, sort })
   )

   readonly updateCases = this.updater((state, { list, count }: PagedList<IAndR>): IAndRListState => ({
      ...state,
      iAndRCases: list,
      count,
      loading: false
   }));

   readonly reload = this.effect((origin$: Observable<GetIAndRsListPayload>) => origin$.pipe(
      tap(payload => this.patchState({ payload, loading: true })),
      switchMap(payload => this.service.getIAndRList(payload).pipe(
         tapResponse(({ count, page, pageSize, list }) => this.updateCases({ list, page, pageSize, count }), err => console.log(err))
      ))
   ))(this.viewState$);

   readonly addIAndR = this.updater((state, iAndR: IAndR) => ({
      ...state,
      iAndRs: state.iAndRCases.filter(c => c.involvementId !== iAndR.involvementId).concat(iAndR)
   }));

   readonly deleteIAndR = this.effect((origin$: Observable<string>) => origin$.pipe(
      tap(() => this.patchState({ loading: true })),
      concatMap(id => this.service.deleteIAndRCase(id)),
      withLatestFrom(this.viewState$),
      concatMap(([_, payload]) => this.service.getIAndRList(payload)),
      tapResponse(({ count, page, pageSize, list }) => this.updateCases({ list, page, pageSize, count }), err => console.log(err))
   ));
}